import { isRef, type Ref, ref, unref, watch } from 'vue';
import { useI18n } from '@/core/plugins/i18n';
import { storage } from '@/core/utils/dom';
import TopPopupWorker from '@/components/popup/lib/worker';
import type { OpenerProps as PopupOpenerProps } from '@/components/popup/popup/types';
import type { Props as TagSelectorOpenerProps, TagSelectorTarget } from '../popupOpener/types';
import type { Props as TagIconProps } from '../tagIcon/types';
import { genTagIdClear, getTagById } from './utils';

/**
 * Сгенерировать элемент для открытия popup с выбором тегов
 *
 * Альтернатива TopTagSelectorPopupOpener для работы вне vue
 *
 * Требует монтирования компонента TopTagSelector
 */
export const genElPopupOpener = (
	props: TagSelectorOpenerProps & { modelValue: TagSelectorOpenerProps['modelValue'] | Ref<TagSelectorOpenerProps['modelValue']> },
	propsPopup?: Partial<PopupOpenerProps>,
	htmlSetterSeveral?: string,
): HTMLElement => {
	if (!propsPopup) propsPopup = { id: props.id };

	// Значения по умолчанию см. в: `@/components/popup/popup/opener.vue`
	propsPopup.id = props.id;
	propsPopup.pos ??= '3';
	propsPopup.notch ??= true;
	propsPopup.posBy ??= 'fixed';

	const el = TopPopupWorker.genElPopupOpener('div', propsPopup as PopupOpenerProps);

	el.classList.add('top-tagSelector');
	if (props.useTopButton) el.classList.add('top-tagSelector-useTopButton', 'top-button', 'top-color_theme', 'top-as-selector');
	if (!props.useTopButton) el.classList.add('top-tagSelector-custom');
	if (props.mode === 'filter') el.classList.add('top-tagSelector-filter');
	if (props.mode === 'setter' && !props.filters) el.classList.add('top-tagSelector-setter_single');
	if (props.mode === 'setter' && props.filters) el.classList.add('top-tagSelector-setter_several');

	// прокcи клик: lazy connect
	el.onclick = (e) => {
		e.preventDefault();
		e.stopPropagation();

		el.onclick = null;

		const model = ref(props.modelValue);

		const target: TagSelectorTarget = {
			model,
			mode: props.mode,
			targetId: props.targetId,
			filters: props.filters,
			payload: props.payload,
		};

		storage(el, 'topTagSelectorTarget', target);

		delete el.dataset.topPopupDisabled;

		if (!isRef(props.modelValue)) {
			watch(model, () => {
				props.modelValue = model.value;

				elPopupOpenerRender(el, props, htmlSetterSeveral);
			});
		}

		// выполнение клика
		el.click();
	};

	if (isRef(props.modelValue)) {
		watch(props.modelValue, () => elPopupOpenerRender(el, props, htmlSetterSeveral));
	} else {
		storage(el, 'topTagSelectorRender', (modelValue: TagSelectorOpenerProps['modelValue']) => {
			props.modelValue = modelValue;

			const target = storage(el, 'topTagSelectorTarget');
			if (target) target.model.value = modelValue;

			elPopupOpenerRender(el, props, htmlSetterSeveral);
		});
	}

	elPopupOpenerRender(el, props, htmlSetterSeveral);

	return el;
};

/**
 * Вызвать перерисовку элемента, который был создан через genElPopupOpener()
 *
 * Работет только для элементов не с реактивным modelValue
 */
export const renderElPopupOpener = (el: HTMLElement, modelValue: TagSelectorOpenerProps['modelValue']) => {
	storage(el, 'topTagSelectorRender')?.(modelValue);
};

/**
 * Отрисовать TopTagSelecorPopupOpener
 */
const elPopupOpenerRender = (el, props: TagSelectorOpenerProps, htmlSetterSeveral?: string) => {
	const tagsIds = unref(props.modelValue);

	el.classList.toggle('top-tagSelector-selectedOne', !tagsIds.length || tagsIds.length === 1);
	el.classList.toggle('top-tagSelector-toTwoLine', tagsIds.length > 5);

	if (props.mode === 'setter' && props.filters) {
		el.innerHTML = `<div>${htmlSetterSeveral}</div>`;

		return;
	}

	el.innerHTML = '';

	if (!tagsIds.length && props.mode === 'filter') {
		const elTagIcon = genElTagIcon({
			id: 'all',
			colorId: '',
			name: useI18n().Common?.All_tags ?? '',
			state: '',
		});

		el.append(elTagIcon);
	}

	tagsIds.forEach(tagId => {
		const elTagIcon = genElTagIcon({
			id: genTagIdClear(tagId),
			colorId: getTagById(tagId, props.tags)?.color_id ?? '',
			name: getTagById(tagId, props.tags)?.name ?? '',
			state: genTagIdClear(tagId) === tagId ? 'selected' : 'excluded',
		});

		el.append(elTagIcon);
	});
};

/**
 * Сгенерировтаь элемент для открытия popup с выбором тегов
 *
 * Альтернатива TopTagSelecorTagIcon для работы вне vue
 */
const genElTagIcon = (props: TagIconProps) => {
	const el = document.createElement('div');

	el.classList.add('top-tagSelector_tagIcon');
	el.classList.toggle('top-tagSelector-active', !!props.state);
	el.classList.toggle('top-tagSelector-excluded', props.state === 'excluded');

	el.dataset.tag_id = props.id;
	el.dataset.tag_color_id = props.colorId;
	el.title = props.name;

	return el;
};
